/**
 * Signup — the shared pre-verified flow from @puglieseweb/shared-react-auth.
 *
 * The shared Cognito pool's PreSignUp trigger REJECTS bare signUp calls:
 * the email must first be verified through shared-auth's
 * /verification/send + /verification/verify (which also sends the
 * systemdox-branded code email from no-reply@systemdox.com), after which
 * PreSignUp auto-confirms — Cognito's own confirmation email never fires.
 * createSignupPage runs that whole flow; the workspace slug is passed to
 * Cognito as clientMetadata.slug for PostConfirmation tenant provisioning.
 *
 * The page's two-column layout (form + "Why create a SystemDox account?")
 * comes from the shared package; the AuthHeader keeps the product chrome,
 * so no second logo is rendered in the form column.
 */
import { createSignupPage, createAuthApi, type SignupBenefit } from '@puglieseweb/shared-react-auth';
import { useAuthContext } from '@/contexts/AuthContext';
import { AuthHeader } from '@/components/layout/AuthHeader';
import { AUTH_API_URL } from '@/lib/apiConfig';
import { launchHostSuffix } from '@/utils/subdomain';
import { tenantApi } from '@/services/tenantApi';

const authApi = createAuthApi({ baseUrl: AUTH_API_URL, product: 'systemdox' });

const BENEFITS: SignupBenefit[] = [
  {
    title: 'Your own workspace',
    description: `A ${launchHostSuffix().replace(/^\./, '{workspace}.')} home for your team’s architecture decisions, standards and domain knowledge.`,
  },
  {
    title: 'Built for AI coding agents',
    description: 'Structured, machine-readable documentation your agents consume over MCP and static exports.',
  },
  {
    title: 'In sync with GitHub',
    description: 'Connect repositories so the documentation tracks the code, with staleness detection when it drifts.',
  },
];

export default createSignupPage({
  brandName: 'SystemDox',
  authApi,
  useAuthContext,
  redirectPath: '/launch',
  header: <AuthHeader />,
  logo: null,
  emailLabel: 'Work email',
  benefits: BENEFITS,
  // Mirrors shared-brand brands.json → systemdox.links
  legal: {
    privacyUrl: 'https://www.systemdox.com/privacy',
    termsUrl: 'https://www.systemdox.com/terms',
  },
  workspaceSlug: {
    hostSuffix: launchHostSuffix(),
    checkAvailability: (slug) => tenantApi.checkSlug(slug),
  },
  // Email only. Google and Apple sign in to an account; they never create
  // one (docs-company ADR-061 Amendment 10, 2026-10-05): shared-auth's
  // PreSignUp refuses a stranger's first Google or Apple contact, because
  // Cognito cannot tell it whether the round trip started here or on the
  // sign-in page. A provider button here would hand every new customer that
  // refusal (and it never carried the workspace slug, which travels as
  // clientMetadata). After this sign-up, Google and Apple on the managed
  // login link into the account by its verified address.
  socialProviders: [],
});
